import classnames from 'classnames';
import React, { FunctionComponent, useCallback, useMemo, useRef } from 'react';
import { useInView } from 'react-intersection-observer';
import {
    useAnalytics,
    useAuthManager,
    TarifficatorPurchaseSuccessWidgetEvent,
} from '@funtech-int/yango-shared';

import { ShowcaseId } from '@/shared/lib/analytics/showcase';
import { renderWithBr } from '@/shared/lib/renderWithBr';
import { ArrowBottom } from '@/shared/components/ArrowBottom/ArrowBottom';
import { Button } from '@/shared/components/Button/Button';
import { PayVia } from '@/shared/components/PayVia/PayVia';
import { useOfferWebAnalytics } from '@/shared/hooks/useOfferWebAnalytics';
import { useSubscriptionRefetch } from '@/shared/hooks/useSubscriptionRefetch';
import { getAcqOfferPlanType } from '@/shared/lib/analytics';
import { usePaymentWidget } from '@/shared/hooks/usePaymentWidget';
import { InterpolatedOfferText } from '@/shared/components/InterpolatedOfferText/InterpolatedOfferText';
import { getPaymentIcons } from '@/shared/lib/payments';

import { createSubscriptionOfferAnalytics } from '../../lib/subscriptionOfferAnalytics';
import { usePaywallContext } from '../../hooks/usePaywallContext';
import { HomeShowcaseHeader } from '../HomeShowcaseHeader/HomeShowcaseHeader';
import { useMainSectionFallbackBackground } from '../../hooks/useMainSectionFallbackBackground';
import { StaticCover } from '../StaticCover/StaticCover';
import { HomeShowcaseSection } from '../HomeShowcaseSection/HomeShowcaseSection';
import { HomeShowcase } from '../HomeShowcase/HomeShowcase';
import { useI18n } from '../../hooks/useOnboardingI18n';
import { createOnboardingAnalytics } from '../../lib/onboardingAnalytics';
import { getSubscriptionOfferButtonText } from '../../lib/getSubscriptionOfferButtonText';

import styles from './PaywallMainSection.module.css';

export const PaywallMainSection: FunctionComponent = () => {
    const { offer: offerFromContext, paymentPlace } = usePaywallContext();
    const offer = offerFromContext ?? null;
    const mainRef = useRef<HTMLDivElement>(null);
    const i18n = useI18n();
    const { isAuthorized } = useAuthManager();
    const background = useMainSectionFallbackBackground();
    const planType = getAcqOfferPlanType(offer?.catalogCompositeOffer);

    const analytics = useAnalytics(createOnboardingAnalytics);
    const { ref: setElementRef } = useInView({
        triggerOnce: true,
        threshold: 0.8,
        onChange(inView: boolean) {
            if (inView) {
                analytics.showed();
            }
        },
    });

    const scrollToNextSection = useCallback(() => {
        const heightMain =
            (mainRef.current && mainRef.current.offsetHeight + mainRef.current.offsetTop) || 0;

        window.scrollTo({ top: heightMain, behavior: 'smooth' });
    }, [mainRef]);

    const buttonText = getSubscriptionOfferButtonText(
        i18n,
        offer?.offerTexts?.buttonText ?? '',
        !isAuthorized
    );

    const subscriptionOfferAnalytics = useAnalytics(createSubscriptionOfferAnalytics);
    subscriptionOfferAnalytics.setData({
        buttonText,
        offer,
    });

    const { refetch: subscriptionRefetch } = useSubscriptionRefetch();
    const offerWebAnalytics = useOfferWebAnalytics(offer);

    const paymentWidget = usePaymentWidget();

    const controller = useMemo(() => {
        function onPurchaseSuccess(data: TarifficatorPurchaseSuccessWidgetEvent) {
            subscriptionRefetch();
            subscriptionOfferAnalytics.succeeded();
            offerWebAnalytics.purchaseSucceeded(data);
        }
        return {
            onOfferShow: () => {
                if (!offer) return;
                subscriptionOfferAnalytics.showed();
                offerWebAnalytics.offerShowed();
                paymentPlace?.analytics.offerShowed(offer);
            },
            onOfferClick: () => {
                if (!offer) return;
                subscriptionOfferAnalytics.navigated();
                offerWebAnalytics.offerClicked();
                paymentPlace?.analytics.offerClicked(offer);

                paymentWidget.open(paymentPlace, offer, {
                    onPurchaseSuccess,
                });
            },
        };
    }, [
        offer,
        offerWebAnalytics,
        paymentWidget,
        paymentPlace,
        subscriptionOfferAnalytics,
        subscriptionRefetch,
    ]);

    const offerTexts = offer?.offerTexts;
    const buttonTextWithDetails = offerTexts?.buttonTextWithDetails;
    const buttonVariant = isAuthorized ? 'plus' : 'accent';
    const shouldShowAdditionText = Boolean(buttonTextWithDetails && isAuthorized);

    return (
        <div className={classnames(styles.root)} ref={setElementRef}>
            <div className={styles.offerBlock} ref={mainRef}>
                <StaticCover backgroundUrl={background} />
                <div className={styles.offerWrapper}>
                    <h1 className={styles.title}>
                        {renderWithBr(
                            offer?.offerTexts?.customTexts?.paywallTitle,
                            i18n('paywall_title', { br: <br /> })
                        )}
                    </h1>
                    {!planType?.isTrial && (
                        <p className={styles.subTitle}>{i18n('paywall_subtitle')}</p>
                    )}
                    {offer?.target && (
                        <>
                            <Button
                                size={52}
                                variant={buttonVariant}
                                onClick={controller.onOfferClick}
                                data-test-id="SubscriptionPurchaseButton"
                                onShow={controller.onOfferShow}
                            >
                                {buttonText}
                            </Button>

                            {shouldShowAdditionText && (
                                <InterpolatedOfferText
                                    className={styles.purchaseButtonText}
                                    legals={offer?.catalogCompositeOffer?.legalInfo?.items}
                                    text={buttonTextWithDetails}
                                />
                            )}
                            <PayVia
                                paymentIcons={getPaymentIcons(offerTexts?.customTexts)}
                                onClick={controller.onOfferClick}
                            />
                        </>
                    )}
                </div>
                <div className={styles.arrowBottom}>
                    <ArrowBottom
                        onScrollTo={scrollToNextSection}
                        withAnimation={false}
                        title={i18n('onboarding__arrow_button_text')}
                    />
                </div>
            </div>
            <HomeShowcaseSection className={styles.showcase}>
                <HomeShowcase id={ShowcaseId.ONBOARDING} header={<HomeShowcaseHeader />} />
            </HomeShowcaseSection>
        </div>
    );
};
